import { Address } from '../utils/types';
import { distanceMeters, distanceToSegment, outerRings, pointInRing, Ring } from '../utils/geo';

// Detect the OTHER street frontages of a building (corner buildings). Mapbox
// reverse-geocoding with `limit>1` returns the nearest addresses across all
// surrounding streets in a SINGLE call — we then keep only the ones that sit on
// THIS building's footprint and dedupe by street. The primary address picked
// from them (resolvePrimaryAddress) is persisted: card and BuildingView seeds
// carry it into listings and follows, and the add-my-place, estimate and
// edit-listing pickers store it as the listing address (JAA-410).

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Geometry = { type?: string; coordinates?: any } | null | undefined;

// Metres per degree of latitude (≈ constant).
const M_PER_DEG_LAT = 111320;
// STRICT: an address counts as this building's frontage only if its point lies
// inside the footprint. No outward buffer — any buffer reaches into the
// neighbouring building (Helsinki blocks share walls) and shows its address as a
// false corner. If real sides start vanishing, Mapbox is placing points just
// outside the footprint and this needs a SMALL (≤6 m) buffer restored.
const EDGE_BUFFER_M = 0;

// Local equirectangular projection to metres around a reference latitude — good
// enough for the ~tens-of-metres distances we test here.
function toXY(lng: number, lat: number, refLat: number): [number, number] {
  const x = lng * M_PER_DEG_LAT * Math.cos((refLat * Math.PI) / 180);
  const y = lat * M_PER_DEG_LAT;
  return [x, y];
}

export function hasFootprint(geometry: Geometry): boolean {
  return outerRings(geometry).some((ring) => ring.length >= 3);
}

// Geometric centroid (mean of outer-ring vertices). Used to work out which side
// of the building a frontage sits on — NOT `building.centroid`, which on the
// map-click path is the click point, not the building's middle.
export function centroidOf(geometry: Geometry): { lat: number; lng: number } | null {
  const rings = outerRings(geometry);
  let sumLng = 0;
  let sumLat = 0;
  let n = 0;
  for (const ring of rings) {
    // Skip the closing vertex (a ring repeats its first point at the end).
    const last = ring.length > 1 && ring[0][0] === ring[ring.length - 1][0] &&
      ring[0][1] === ring[ring.length - 1][1] ? ring.length - 1 : ring.length;
    for (let i = 0; i < last; i++) {
      sumLng += ring[i][0];
      sumLat += ring[i][1];
      n++;
    }
  }
  if (!n) return null;
  return { lat: sumLat / n, lng: sumLng / n };
}

// Rotation (deg) for the corner glyph "┌" so it marks the building corner the
// frontage faces. The base glyph is top-left (┌); 90° steps rotate it through
// ┐ ┘ └. Direction is centroid → frontage point: north=up, east=right.
//   NW ┌ 0   NE ┐ 90   SE ┘ 180   SW └ 270
export function cornerRotationDeg(
  point: { lat: number; lng: number },
  centroid: { lat: number; lng: number },
): number {
  const east = point.lng - centroid.lng >= 0;
  const north = point.lat - centroid.lat >= 0;
  if (north && !east) return 0; // NW ┌
  if (north && east) return 90; // NE ┐
  if (!north && east) return 180; // SE ┘
  return 270; // SW └
}

// Is an address point on (or within EDGE_BUFFER_M of) the building footprint?
export function pointOnFootprint(pt: number[], geometry: Geometry): boolean {
  const rings = outerRings(geometry);
  if (!rings.length) return false;
  const point = { lat: pt[1], lng: pt[0] };
  for (const ring of rings) {
    if (ring.length < 3) continue;
    if (pointInRing(pt, ring)) return true;
    for (let i = 0; i < ring.length - 1; i++) {
      if (distanceToSegment(point, ring[i], ring[i + 1]) <= EDGE_BUFFER_M) return true;
    }
  }
  return false;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function ctx(type: string, place: any): string {
  const c = place.context?.find((entry: { id: string }) => entry.id.startsWith(type));
  return c ? c.text : '';
}

// Perimeter length (metres) of a ring, via local planar projection.
function perimeterMeters(ring: Ring): number {
  if (ring.length < 2) return 0;
  const refLat = ring[0][1];
  let total = 0;
  let [px, py] = toXY(ring[0][0], ring[0][1], refLat);
  for (let i = 1; i < ring.length; i++) {
    const [x, y] = toXY(ring[i][0], ring[i][1], refLat);
    total += Math.hypot(x - px, y - py);
    px = x;
    py = y;
  }
  return total;
}

// Sample `count` points evenly by ARC LENGTH around a ring — equal spacing along
// the boundary, including the middle of long facades that have few vertices
// (index-sampling would under-probe those).
function sampleByArcLength(ring: Ring, count: number): number[][] {
  if (ring.length < 2 || count < 1) return ring.slice(0, count);
  const refLat = ring[0][1];
  const xy = ring.map((p) => toXY(p[0], p[1], refLat));
  const cum = [0];
  for (let i = 1; i < ring.length; i++) {
    cum.push(cum[i - 1] + Math.hypot(xy[i][0] - xy[i - 1][0], xy[i][1] - xy[i - 1][1]));
  }
  const total = cum[cum.length - 1];
  if (total === 0) return [ring[0]];
  const out: number[][] = [];
  for (let k = 0; k < count; k++) {
    const target = (total * k) / count;
    let i = 1;
    while (i < cum.length && cum[i] < target) i++;
    const seg = cum[i] - cum[i - 1] || 1;
    const t = (target - cum[i - 1]) / seg;
    out.push([
      ring[i - 1][0] + t * (ring[i][0] - ring[i - 1][0]),
      ring[i - 1][1] + t * (ring[i][1] - ring[i - 1][1]),
    ]);
  }
  return out;
}

function sameVertex(a: number[], b: number[]): boolean {
  return a[0] === b[0] && a[1] === b[1];
}

// One reverse-geocode → its raw address features (empty on any failure, so a
// single bad probe never sinks the whole batch).
async function geocodeFeatures(
  lng: number,
  lat: number,
  token: string,
  signal?: AbortSignal,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any[]> {
  try {
    const url =
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json` +
      `?types=address&limit=5&access_token=${token}`;
    const res = await fetch(url, signal ? { signal } : undefined);
    const data = await res.json();
    return data?.features ?? [];
  } catch {
    return [];
  }
}

// One probe per ~PROBE_SPACING_M of facade, clamped — a small building gets ~2
// probes, a whole-block building (shopping centre) gets the max.
const PROBE_SPACING_M = 40;
const MAX_PROBES = 8;

// Mapbox Streets house-number points: the source of the map style's
// `building-number-label` layer, i.e. the number the map prints on a building.
const HOUSENUM_TILEQUERY_URL =
  'https://api.mapbox.com/v4/mapbox.mapbox-streets-v8/tilequery';
// The Tilequery API's maximum feature count.
const TILEQUERY_LIMIT = 50;
// How far a labelled number's geocoder address may lie from the building. A
// multi-building lot keeps ONE geocoder point for all its buildings: ~100 m
// from the farthest building at Mankkaanpuro 13, Espoo (JAA-410).
const LABEL_ADDRESS_MAX_M = 300;
// Side addresses read from a map label (labelledAddresses) rather than a
// geocoder point on the footprint, tracked by object identity so the tag never
// travels into a copied or stored address.
const LABEL_ADDRESSES = new WeakSet<Address>();
// Label addresses whose street is backed by that street's own numbering around
// the label (runsAround), not only by the nearest same-number point.
const STREET_CORROBORATED = new WeakSet<Address>();

/**
 * True when `a` came from the house number the map prints on the footprint
 * (JAA-410), not from a geocoder point on it. A label gives a number but no
 * street, so such an entry is never a second frontage (BuildingView) and never
 * overrides a clicked address that carries the same number, or that number plus
 * one letter (clickedMatchesLabel, resolvePrimaryAddress), unless the label's
 * street is corroborated and the clicked one is another street.
 */
export function isLabelAddress(a: Address | null | undefined): boolean {
  return !!a && LABEL_ADDRESSES.has(a);
}

/** A house number in comparable form: the map's "12 B" equals the geocoder's "12b". */
function normalizeHouseNumber(value: unknown): string {
  return String(value ?? '').replace(/\s+/g, '').toLowerCase();
}

/** A house number's leading integer ("15b" → 15, "17-19" → 17); null when it has none. */
function houseNumberValue(value: unknown): number | null {
  const m = /^\d+/.exec(normalizeHouseNumber(value));
  return m ? Number(m[0]) : null;
}

// How far (in house numbers) the neighbours around a label may be: the next
// building on that side of the street, or the one after it.
const AROUND_MAX_STEP = 4;

/**
 * The street whose own numbering runs around `number`: among `nearby`, the
 * nearest street with numbers of the same parity (the same side of the street)
 * just below and just above it, within AROUND_MAX_STEP. "13" printed between
 * Uudenmaankatu 11 and 15 is Uudenmaankatu 13, not the crossing street's
 * Annankatu 13 (JAA-410). Undefined when no street does.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function streetAround(nearby: { f: any }[], number: string): string | undefined {
  return nearby.find(({ f }) => runsAround(nearby, f.text, number))?.f.text;
}

/**
 * True when `street`'s numbers among `nearby` on `number`'s side (same parity)
 * include one just below and one just above it, within AROUND_MAX_STEP: the
 * street's own numbering places `number` here.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function runsAround(nearby: { f: any }[], street: string, number: string): boolean {
  const value = houseNumberValue(number);
  if (value === null) return false;
  let below = false;
  let above = false;
  for (const { f } of nearby) {
    if (f.text !== street) continue;
    const v = houseNumberValue(f.address);
    if (v === null || v % 2 !== value % 2 || Math.abs(v - value) > AROUND_MAX_STEP) continue;
    if (v < value) below = true;
    if (v > value) above = true;
  }
  return below && above;
}

/** Where a geocoder feature sits, as [lng, lat], or null when it has no point. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function featurePoint(f: any): number[] | null {
  return f?.center ?? f?.geometry?.coordinates ?? null;
}

/** A geocoder address feature as an Address pinned at `pt`. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function featureToAddress(f: any, pt: number[]): Address {
  return {
    country: ctx('country', f),
    region: ctx('region', f),
    city: ctx('place', f),
    postcode: ctx('postcode', f),
    street: f.text,
    houseNumber: f.address || 'Unknown',
    coordinates: { lat: pt[1], lng: pt[0] },
  };
}

/**
 * The house number the map prints on this footprint and the point it is printed
 * at: the Mapbox Streets `housenum_label` point inside the polygon nearest the
 * centre. The geocoder keeps one point per address, so on a multi-building lot
 * most buildings hold no point of their own and the nearest one is a
 * neighbour's (JAA-410); the label sits on every building. Null when the
 * footprint carries no label, when its labels carry different numbers (a
 * corner or multi-entrance building, where the street of each number is
 * unknown), and on any failure.
 */
async function footprintHouseNumber(
  geometry: Geometry,
  center: { lat: number; lng: number },
  token: string,
  signal?: AbortSignal,
): Promise<{ number: string; pt: number[] } | null> {
  let radius = 0;
  for (const ring of outerRings(geometry)) {
    for (const v of ring) {
      radius = Math.max(radius, distanceMeters(center, { lng: v[0], lat: v[1] }));
    }
  }
  try {
    const url =
      `${HOUSENUM_TILEQUERY_URL}/${center.lng},${center.lat}.json` +
      `?layers=housenum_label&radius=${Math.ceil(radius) + 1}` +
      `&limit=${TILEQUERY_LIMIT}&access_token=${token}`;
    const res = await fetch(url, signal ? { signal } : undefined);
    const data = await res.json();
    let label: { number: string; pt: number[] } | null = null;
    for (const f of data?.features ?? []) {
      const pt = f?.geometry?.coordinates;
      if (!Array.isArray(pt) || !pointOnFootprint(pt, geometry)) continue;
      const n = normalizeHouseNumber(f.properties?.house_num);
      if (!n) continue;
      if (label && label.number !== n) return null;
      if (!label) label = { number: n, pt };
    }
    return label;
  } catch {
    return null;
  }
}

/**
 * Forward-geocode "street number" biased to `center`. Returns the feature only
 * when it is exactly that street and number and lies within
 * LABEL_ADDRESS_MAX_M of `center`, so a same-named street elsewhere never
 * passes; null otherwise or on any failure.
 */
async function geocodeStreetNumber(
  street: string,
  number: string,
  center: { lat: number; lng: number },
  token: string,
  signal?: AbortSignal,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any | null> {
  try {
    const query = encodeURIComponent(`${street} ${number}`);
    const url =
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${query}.json` +
      `?types=address&limit=1&proximity=${center.lng},${center.lat}&access_token=${token}`;
    const res = await fetch(url, signal ? { signal } : undefined);
    const f = (await res.json())?.features?.[0];
    const pt = featurePoint(f);
    if (!f || !pt) return null;
    if (String(f.text ?? '').toLowerCase() !== street.toLowerCase()) return null;
    if (normalizeHouseNumber(f.address) !== number) return null;
    if (distanceMeters(center, { lng: pt[0], lat: pt[1] }) > LABEL_ADDRESS_MAX_M) return null;
    return f;
  } catch {
    return null;
  }
}

/**
 * The building's address read from the number the map prints on it, for when
 * no geocoder point lands on the footprint: the nearest probe result carrying
 * that number. When the numbers of a street nearer than that result run around
 * the label (streetAround), only a result on that street counts, since the
 * nearest one is then a crossing street's. Else a forward geocode of the
 * number on that street, or on the street of the nearest probe result on the
 * number's side of the street (on a multi-building lot the nearest point is a
 * same-street neighbour's, at a corner it can be a crossing street's); either
 * must lie within LABEL_ADDRESS_MAX_M. The
 * address is pinned to the label's point, so like every other side address it
 * sits on this building: the geocoder's own point can lie on another building
 * of the lot, and a listing seeded from this address takes its location from
 * these coordinates. Empty when the footprint carries no single number, which
 * keeps the clicked address.
 */
async function labelledAddresses(
  geometry: Geometry,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  features: any[],
  token: string,
  signal?: AbortSignal,
): Promise<Address[]> {
  const center = centroidOf(geometry);
  if (!center) return [];
  const label = await footprintHouseNumber(geometry, center, token, signal);
  if (!label) return [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const nearby: { f: any; d: number }[] = [];
  for (const f of features) {
    const pt = featurePoint(f);
    if (!f.text || !pt) continue;
    const d = distanceMeters(center, { lng: pt[0], lat: pt[1] });
    if (d <= LABEL_ADDRESS_MAX_M) nearby.push({ f, d });
  }
  nearby.sort((a, b) => a.d - b.d);
  const hitAt = nearby.findIndex(({ f }) => normalizeHouseNumber(f.address) === label.number);
  const around = streetAround(hitAt < 0 ? nearby : nearby.slice(0, hitAt), label.number);
  const hit = nearby.find(
    ({ f }) => (!around || f.text === around) && normalizeHouseNumber(f.address) === label.number,
  );
  if (hit) return [labelAddress(hit.f, label.pt, runsAround(nearby, hit.f.text, label.number))];
  const value = houseNumberValue(label.number);
  const sameSide = nearby.find(({ f }) => {
    const v = houseNumberValue(f.address);
    return value !== null && v !== null && v % 2 === value % 2;
  });
  const street = around ?? (sameSide ?? nearby[0])?.f.text;
  if (!street) return [];
  const f = await geocodeStreetNumber(street, label.number, center, token, signal);
  return f ? [labelAddress(f, label.pt, runsAround(nearby, street, label.number))] : [];
}

/**
 * A label address pinned to the label's point, tagged STREET_CORROBORATED when
 * its street's own numbers run around the label among the nearby points
 * (runsAround), so resolvePrimaryAddress can keep that street over a
 * same-numbered crossing street (JAA-410).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function labelAddress(f: any, pt: number[], corroborated: boolean): Address {
  const a = featureToAddress(f, pt);
  if (corroborated) STREET_CORROBORATED.add(a);
  return a;
}

/**
 * True when `label`'s street is corroborated by that street's own numbering
 * and `clicked` names another street: the clicked address is then a crossing
 * street's same-numbered address (Annankatu 13 at the Uudenmaankatu 13
 * corner), so it must not stand in for the label (JAA-410).
 */
function crossesCorroboratedStreet(clicked: Address, label: Address): boolean {
  if (!STREET_CORROBORATED.has(label)) return false;
  return (clicked.street ?? '').trim().toLowerCase() !== (label.street ?? '').trim().toLowerCase();
}

// Normalized full-address key (street + number). Keying on this instead of the
// street keeps a building's multiple same-street entrances distinct — e.g.
// "Ruoholahdenkatu 16" and "Ruoholahdenkatu 18" are two addresses, not one.
export function addressKey(
  a: { street?: string; houseNumber?: string } | null | undefined,
): string {
  if (!a || !a.street) return '';
  const num = a.houseNumber && a.houseNumber !== 'Unknown' ? ` ${a.houseNumber}` : '';
  return `${a.street}${num}`.trim().toLowerCase();
}

/**
 * Pick the address whose point is nearest `point` (planar distance — fine at
 * building scale). Used to choose the PRIMARY address from the footprint-filtered
 * set (fetchSideAddresses), so the heading is GUARANTEED to sit on this building,
 * not a neighbour across a shared wall. Returns null for an empty list.
 */
export function nearestAddressTo(
  addresses: Address[],
  point: { lat: number; lng: number } | null | undefined,
): Address | null {
  if (!addresses.length) return null;
  if (!point || !Number.isFinite(point.lat) || !Number.isFinite(point.lng)) return addresses[0];
  let best: Address | null = null;
  let bestD = Infinity;
  for (const a of addresses) {
    const c = a.coordinates;
    if (!c || !Number.isFinite(c.lat) || !Number.isFinite(c.lng)) continue;
    const dLng = c.lng - point.lng;
    const dLat = c.lat - point.lat;
    const d = dLng * dLng + dLat * dLat;
    if (d < bestD) {
      bestD = d;
      best = a;
    }
  }
  return best ?? addresses[0];
}

/**
 * True when the clicked address carries the map label's number: the same
 * number, or, when the label has no letter, that number plus one letter. A
 * bare label "5" covers every house of a lettered lot (5, 5a, 5b), so a
 * clicked "5b" names this house more precisely than the lot's "5" and is kept
 * (JAA-410 sweep, Metsänvartijantie 5b, Espoo). Like the exact match, the
 * street is not compared: the label vouches for the number only. The clicked
 * address's own geocoder point is never on this footprint (the label path runs
 * only when no geocoder point is), so keeping it keeps exactly what master
 * showed.
 */
function clickedMatchesLabel(clicked: Address, label: Address): boolean {
  const c = normalizeHouseNumber(clicked.houseNumber);
  const l = normalizeHouseNumber(label.houseNumber);
  if (c === l) return true;
  const m = /^(\d+)[a-zåäö]$/.exec(c);
  return !!m && m[1] === l;
}

/**
 * Choose the PRIMARY address. Prefer a footprint-verified frontage; fall back to
 * the building's own clicked address when none verifies.
 *
 * `clicked` is the reverse-geocode of a point ON this building (the map click /
 * centroid), so it is normally this building's address. When `sides` (the
 * footprint-verified frontages) is non-empty, `clicked` is trusted only if its
 * exact street+number is a member — otherwise it is corrected to the nearest
 * verified frontage, which guards against a shared-wall neighbour's name. When
 * `sides` is empty we surface `clicked` rather than nothing. `sides` is empty
 * only when no geocoded point lands on the polygon AND the map prints no single
 * house number on it that resolves to an address (fetchSideAddresses), so a
 * multi-building lot's neighbour address is corrected (JAA-410). A side read
 * from the map label (isLabelAddress) vouches for the number only, not the
 * street, so it replaces `clicked` only when the clicked number is not the
 * label's number or that number plus one letter (clickedMatchesLabel): a right
 * clicked address is not swapped for a same-numbered crossing street, unless
 * the label's street is corroborated by its own numbering (runsAround). Then a
 * clicked address on another street no longer counts as matching
 * (crossesCorroboratedStreet): it is the crossing street's, and the label wins.
 * The residual risk, an unlabelled or multi-numbered building reverse-geocoding
 * to a neighbour, is accepted over a permanently missing address. So is a
 * lettered click, kept as master would keep it: a tap near the geocoder point
 * "Bulevardi 15b" on the Bulevardi 15 courtyard wing (OSM 123586002) reads
 * "Bulevardi 15b", not "Bulevardi 15" (Kane's decision 2026-10-14).
 */
export function resolvePrimaryAddress(
  clicked: Address | null | undefined,
  sides: Address[],
  point: { lat: number; lng: number } | null | undefined,
): Address | null {
  if (sides.length) {
    const clickedKey = addressKey(clicked);
    if (clickedKey && sides.some((a) => addressKey(a) === clickedKey)) {
      return clicked ?? null;
    }
    const label = isLabelAddress(sides[0]) ? sides[0] : null;
    if (clicked && label && clickedMatchesLabel(clicked, label) && !crossesCorroboratedStreet(clicked, label)) {
      return clicked;
    }
    return nearestAddressTo(sides, point);
  }
  return clicked ?? null;
}

/**
 * Fetch the distinct street frontages of a building — footprint-verified only.
 *
 * A building can front more streets than one reverse-geocode's `limit=5` can
 * return (a whole-block building fronts 6+), so we PROBE around the perimeter —
 * one reverse-geocode per ~40 m of facade — then union, KEEP ONLY the points
 * that land on this footprint (strict point-in-polygon), and dedupe by street.
 *
 * When no geocoded point lands on the footprint (the geocoder keeps one point
 * per address, often at the street door or on another building of the same
 * lot), the address comes from the house number the map prints on the
 * footprint instead, pinned to the label's point and tagged for isLabelAddress
 * (labelledAddresses, JAA-410).
 *
 * With NO footprint we return nothing: without a polygon we can't tell a
 * neighbour's address from this building's, and a missing frontage is preferable
 * to a wrong one.
 */
export async function fetchSideAddresses(
  center: { lat: number; lng: number },
  geometry: Geometry,
  token: string,
  signal?: AbortSignal,
): Promise<Address[]> {
  if (!hasFootprint(geometry)) return [];

  const rings = outerRings(geometry);
  const ring = rings.reduce((a, b) => (b.length >= a.length ? b : a), rings[0]);
  const open =
    ring.length > 1 && sameVertex(ring[0], ring[ring.length - 1])
      ? ring.slice(0, -1)
      : ring;
  const count = Math.max(
    2,
    Math.min(MAX_PROBES, Math.round(perimeterMeters(open) / PROBE_SPACING_M)),
  );
  const probes = sampleByArcLength(open, count);
  const c = centroidOf(geometry);
  if (c) probes.push([c.lng, c.lat]);

  const lists = await Promise.all(
    probes.map((p) => geocodeFeatures(p[0], p[1], token, signal)),
  );
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const features: any[] = lists.flat();

  const out: Address[] = [];
  const seen = new Set<string>();
  for (const f of features) {
    const street: string = f.text;
    if (!street) continue;
    const pt = featurePoint(f);
    if (!pt || !pointOnFootprint(pt, geometry)) continue;
    const key = addressKey({ street, houseNumber: f.address || 'Unknown' });
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(featureToAddress(f, pt));
  }

  if (out.length) return out;
  const labelled = await labelledAddresses(geometry, features, token, signal);
  labelled.forEach((a) => LABEL_ADDRESSES.add(a));
  return labelled;
}
